import type { CityScopeAnalyticsEvent } from './analyticsJourneys';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export interface Ga4TrackedEvent {
  eventName: string;
  params: Record<string, string | number | boolean>;
}

const GA4_ACTION_EVENT_NAMES: Record<string, string> = {
  application_opened: 'application_opened',
  planning_updates_viewed: 'planning_updates_viewed',
  search_submitted: 'search_submitted',
  planning_updates_search_term_submitted: 'planning_updates_search_term_submitted',
  report_signup_started: 'report_signup_started',
  report_signup_submit_attempted: 'report_signup_submit_attempted',
  report_signup_submitted: 'report_signup_submitted',
  report_signup_accepted: 'report_signup_accepted',
  sign_up_completed: 'sign_up_completed',
  document_thumbnail_opened: 'document_thumbnail_opened',
  council_record_opened: 'council_record_opened',
};

const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/i;
const IPV4_PATTERN = /\b(?:\d{1,3}\.){3}\d{1,3}\b/;
const RAW_TEXT_KEY_PATTERN =
  /(^|_)(raw|email|ip|visitor_id|user_id|client_id|query_text|search_text|input_value|input_text|typed_value|typed_text)($|_)/i;

function shouldEnableGa4Tracking(): boolean {
  if (typeof window === 'undefined') return false;
  return process.env.NEXT_PUBLIC_GA_TRACKING_ENABLED === '1' ||
    process.env.NEXT_PUBLIC_GA_TRACKING_ENABLED === 'true';
}

function isUnsafeString(value: string): boolean {
  return EMAIL_PATTERN.test(value) || IPV4_PATTERN.test(value) || /^https?:\/\//i.test(value);
}

function cleanString(value: unknown, maxLength = 100): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed || isUnsafeString(trimmed)) return undefined;

  const cleaned = trimmed
    .toLowerCase()
    .replace(/https?:\/\/[^/\s]+/g, '')
    .replace(/[^a-z0-9_:/-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, maxLength);

  return cleaned || undefined;
}

function cleanParamName(value: string): string | undefined {
  const cleaned = cleanString(value, 40)?.replace(/[^a-z0-9_]+/g, '_');
  if (!cleaned || RAW_TEXT_KEY_PATTERN.test(cleaned)) return undefined;
  return /^[a-z]/.test(cleaned) ? cleaned : `p_${cleaned}`.slice(0, 40);
}

function cleanParamValue(value: unknown): string | number | boolean | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'boolean') return value;
  return cleanString(value);
}

function setParam(
  params: Record<string, string | number | boolean>,
  key: string,
  value: unknown,
): void {
  const cleanKey = cleanParamName(key);
  if (!cleanKey) return;
  const cleanValue = cleanParamValue(value);
  if (cleanValue !== undefined) params[cleanKey] = cleanValue;
}

function categoryJourney(category: string): string | undefined {
  const cleanCategory = cleanString(category, 80);
  if (!cleanCategory) return undefined;
  return cleanCategory.startsWith('journey:') ? cleanCategory.slice('journey:'.length) : cleanCategory;
}

function searchTermForEvent(event: CityScopeAnalyticsEvent): string | undefined {
  const metadata = event.metadata || {};
  const candidates = [
    metadata.search_term,
    metadata.term,
    metadata.entity_id,
    event.name,
  ];
  for (const candidate of candidates) {
    const cleaned = cleanString(candidate, 80);
    if (cleaned) return cleaned;
  }
  return undefined;
}

export function buildGa4Event(event: CityScopeAnalyticsEvent): Ga4TrackedEvent | null {
  const eventName = GA4_ACTION_EVENT_NAMES[event.action];
  if (!eventName) return null;

  const metadata = event.metadata || {};
  const params: Record<string, string | number | boolean> = {
    cityscope_action: event.action,
  };

  const journey = cleanParamValue(metadata.journey) || categoryJourney(event.category);
  if (journey) params.journey = journey;

  setParam(params, 'cityscope_category', event.category);
  setParam(params, 'cityscope_label', event.name);
  setParam(params, 'page_type', metadata.page_type);
  setParam(params, 'source_council', metadata.source_council || metadata.council_slug || metadata.council);
  setParam(params, 'source', metadata.source);
  setParam(params, 'placement', metadata.placement);
  setParam(params, 'status', metadata.status);
  setParam(params, 'reason', metadata.reason);
  setParam(params, 'entity_type', metadata.entity_type);
  setParam(params, 'report_key', metadata.report_key);
  setParam(params, 'mode', metadata.mode);
  setParam(params, 'source_action', event.action === eventName ? undefined : event.action);

  if (event.action === 'planning_updates_search_term_submitted') {
    setParam(params, 'search_term', searchTermForEvent(event));
  } else {
    setParam(params, 'entity_id', metadata.entity_id);
  }

  if (typeof event.value === 'number' && Number.isFinite(event.value)) {
    params.value = event.value;
  }

  return { eventName, params };
}

export function trackGa4Event(event: CityScopeAnalyticsEvent): void {
  if (!shouldEnableGa4Tracking()) return;
  const tracked = buildGa4Event(event);
  if (!tracked) return;

  if (typeof window.gtag === 'function') {
    window.gtag('event', tracked.eventName, tracked.params);
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(['event', tracked.eventName, tracked.params]);
}
